

import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import {
  FiEdit,
  FiTrash2,
  FiKey,
  FiChevronLeft,
  FiChevronRight,
  FiSave,
  FiX,
  FiEye,
} from "react-icons/fi";
import { API_URL } from "../../Constants";
import { AuthContext } from "../context/AuthContext";
import { toast } from "react-toastify";
import { FaRupeeSign } from "react-icons/fa";
import { FaEye as FaEyeSolid, FaEyeSlash } from "react-icons/fa";

const ViewAccount = () => {
  const { user } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  // Permission modal state
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [currentUserPermissions, setCurrentUserPermissions] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [permissionLoading, setPermissionLoading] = useState(false);
  const [permissionError, setPermissionError] = useState(null);
  // Add these new states at the top of your component
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentEditUser, setCurrentEditUser] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState(null);
  // Add these new states at the top of your component
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [showAmountModal, setShowAmountModal] = useState(false);
  const [currentAmountUser, setCurrentAmountUser] = useState(null);
  const [amountLoading, setAmountLoading] = useState(false);
  const [amountError, setAmountError] = useState(null);
  // Add these state variables at the top of ViewAccount component
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUserForPasswordChange, setSelectedUserForPasswordChange] =
    useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // New states for Transcription API Key feature
  const [transcriptionMode, setTranscriptionMode] = useState("DEFAULT");
  const [isTranscriptionLoading, setIsTranscriptionLoading] = useState(false);

  const isBusinessAccount = [
    "SUPER_SUPER_ADMIN",
    "SUPER_ADMIN",
    // "COMPANY",
    "RESELLER",
  ].includes(user?.accountType);

  // Check if current user is Reseller, SUPER_ADMIN, or SUPER_SUPER_ADMIN
  const showSpecialFields = [
    "RESELLER",
    "SUPER_ADMIN",
    "SUPER_SUPER_ADMIN",
  ].includes(user?.accountType);

  const fetchUsers = async () => {
    if (!user?.id || !user?.accountType) return;

    try {
      const url =
        user?.accountType === "SUPER_SUPER_ADMIN" ||
        user?.accountType === "SUPER_ADMIN" ||
        user?.accountType === "RESELLER"
          ? `${API_URL}/api/user/getByUserId?userId=${user.id}&accountType=${user.accountType}`
          : `${API_URL}/api/companyuser/getByUserId?userId=${user.id}&accountType=${user.accountType}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });
      setUsers(response.data);
      setError(null);
    } catch (error) {
      console.error("Error fetching users:", error);
      setError("Failed to fetch users. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [user]);

  const hasPermission = (permission) => {
    return permissions && Object.keys(permissions).includes(permission);
  };

  const fetchPermissions = async () => {
    if (!user?.id) return;

    try {
      const url =
        user?.accountType === "SUPER_SUPER_ADMIN" ||
        user?.accountType === "COMPANY" ||
        user?.accountType === "SUPER_ADMIN" ||
        user?.accountType === "RESELLER"
          ? `${API_URL}/api/user/details/${user.id}`
          : `${API_URL}/api/companyuser/details/${user.id}`;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      const fetchedPermissions = response.data.permission[0] || {};
      setPermissions(fetchedPermissions);
      localStorage.setItem(
        "userPermissions",
        JSON.stringify(fetchedPermissions),
      );
    } catch (error) {
      console.error("Error fetching permissions:", error);
    }
  };

  useEffect(() => {
    fetchPermissions();
  }, [user?.id]);

  const handlePermissions = async (userId) => {
    try {
      setPermissionLoading(true);
      setPermissionError(null);
      // Reset transcription modal states
      setTranscriptionMode("DEFAULT");

      const url =
        user?.accountType === "SUPER_SUPER_ADMIN" ||
        user?.accountType === "SUPER_ADMIN" ||
        user?.accountType === "RESELLER"
          ? `${API_URL}/api/user/details/${userId}`
          : `${API_URL}/api/companyuser/details/${userId}`;

      // Fetch the user's permissions from the API
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      const userData = response.data;

      if (userData.permission && userData.permission.length > 0) {
        setCurrentUserPermissions({
          userId: userId,
          ...userData.permission[0],
        });
      } else {
        // Create new permissions object if none exist
        setCurrentUserPermissions({
          userId: userId,
          permissionId: null,
          accountCreatedById: user.id,
          createAccount: false,
          viewAccount: false,
          dashboard: false,
          leads: false,
          leadTask: false,
          task: false,
          reminder: false,
          Whatsapp: false,
          meeting: false,
          videoCall: false,
          todo: false,
          notes: false,
          nnAcresConfig: false,
          calendar: false,
          invoice: false,
          hr: false,
          customer: false,
          greetings: false,
          googleForm: false,
          generalsettings: false,
          clickToCall: false,
          attributes: false,
          whatsappIntegration: false,
          leadStatusAttributes: false,
          leadSourceAttributes: false,
          projectNameAttributes: false,
          taskLabelAttributes: false,
          taskStatusAttributes: false,
          environmentAttributes: false,
          severityAttributes: false,
          templates: false,
          issuesStatusAttributes: false,
          issuesType: false,
          nnAcresConfig: false, // Intentionally left duplicated to match original code
          whatsappTemplates: false,
          smsTemplates: false,
          emailTemplates: false,
          greetingTemplates: false,
          automation: false,
          emailAutomation: false,
          whatsappAutomation: false,
          smsAutomation: false,
          leadEdit: false, // Add leadEdit here
          downloadReport: false,
        });
      }

      setShowPermissionModal(true);
    } catch (error) {
      console.error("Error loading permissions:", error);
      setPermissionError("Failed to load permissions. Please try again.");
    } finally {
      setPermissionLoading(false);
    }
  };

  // Transcription API Mode Handling
  const updateTranscriptionMode = async (newMode) => {
    try {
      setIsTranscriptionLoading(true);
      const targetCompanyId = currentUserPermissions.userId; 
      await axios.put(
        `${API_URL}/api/gemini-api-key/${targetCompanyId}/mode?mode=${newMode}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setTranscriptionMode(newMode);
      toast.success(`Transcription mode updated to ${newMode}`);
    } catch (error) {
      console.error("Error updating transcription mode:", error);
      toast.error("Failed to update transcription mode");
    } finally {
      setIsTranscriptionLoading(false);
    }
  };

  const handleAmount = (user) => {
    setCurrentAmountUser({
      ...user,
      balanceExpiryDate: user.balanceExpiryDate || "",
    });
    setShowAmountModal(true);
  };

  // Add this function to save amount changes
  const saveAmount = async () => {
    try {
      setAmountLoading(true);
      setAmountError(null);

      const payload = {
        staffLimit: currentAmountUser.staffLimit,
        smsSendRate: currentAmountUser.smsSendRate,
        emailSendRate: currentAmountUser.emailSendRate,
        whatsappSmsRate: currentAmountUser.whatsappSmsRate,
        totalSMSAmount: currentAmountUser.totalSMSAmount,
        totalEmailAmount: currentAmountUser.totalEmailAmount,
        totalWhatsaappAmount: currentAmountUser.totalWhatsaappAmount,
        balanceExpiryDate: currentAmountUser.balanceExpiryDate,
      };

      const response = await axios.put(
        `${API_URL}/api/user/ammount/update/${currentAmountUser.email}/${user.id}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        },
      );

      // Update the user in the local state
      setUsers((prevUsers) =>
        prevUsers.map((u) =>
          u.id === currentAmountUser.id ? { ...u, ...response.data } : u,
        ),
      );

      toast.success("Amount details updated successfully");
      setShowAmountModal(false);
    } catch (error) {
      console.error("Error updating amount details:", error);
      setAmountError("Failed to update amount details. Please try again.");
      toast.error("Failed to update amount details");
    } finally {
      setAmountLoading(false);
    }
  };

  // Handle permission toggle
  const handlePermissionToggle = (field) => {
    setCurrentUserPermissions((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  // Save permissions
  const savePermissions = async () => {
    try {
      setPermissionLoading(true);
      setPermissionError(null);

      // Prepare the payload
      const payload = { ...currentUserPermissions };

      // Remove userId from the payload as it's not part of the permission schema
      // delete payload.userId;

      // Determine the API URL based on the account type
      const url =
        user?.accountType === "SUPER_SUPER_ADMIN" ||
        user?.accountType === "SUPER_ADMIN" ||
        user?.accountType === "RESELLER"
          ? `${API_URL}/api/userpermissions/${currentUserPermissions.permissionId}`
          : `${API_URL}/api/companyUser/permission/${currentUserPermissions.permissionId}`;

      const response = await axios.put(url, payload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });

      // Update the user's permissions in the local state
      setUsers((prevUsers) =>
        prevUsers.map((u) =>
          u.id === currentUserPermissions.userId
            ? { ...u, permission: [response.data] }
            : u,
        ),
      );

      // Close the modal on success
      setShowPermissionModal(false);
    } catch (error) {
      console.error("Error saving permissions:", error);
      setPermissionError("Failed to save permissions. Please try again.");
    } finally {
      setPermissionLoading(false);
    }
  };

  const handleEdit = async (userId) => {
    try {
      setEditLoading(true);
      setEditError(null);

      const url =
        user?.accountType === "SUPER_SUPER_ADMIN" ||
        user?.accountType === "SUPER_ADMIN" ||
        user?.accountType === "RESELLER"
          ? `${API_URL}/api/user/details/${userId}`
          : `${API_URL}/api/companyuser/details/${userId}`;

      // Fetch the user's details from the API
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      const userData = response.data;
      setCurrentEditUser({
        ...userData,
        isActive: userData.isActive !== false, // Ensure boolean value
      });

      setShowEditModal(true);
    } catch (error) {
      console.error("Error loading user details:", error);
      setEditError("Failed to load user details. Please try again.");
    } finally {
      setEditLoading(false);
    }
  };

  // Add the saveEdit function
  const saveEdit = async () => {
    try {
      setEditLoading(true);
      setEditError(null);

      // Determine the API URL based on the account type
      const url =
        user?.accountType === "SUPER_SUPER_ADMIN" ||
        user?.accountType === "SUPER_ADMIN" ||
        user?.accountType === "RESELLER"
          ? `${API_URL}/api/user/update/${currentEditUser.email}`
          : `${API_URL}/api/companyuser/updateCompanyUserProfile/${currentEditUser.id}/changedBy/${user?.id}`;

      // Prepare payload with isActive status
      const payload = {
        ...currentEditUser,
        isActive: currentEditUser.isActive !== false, // Ensure boolean value
      };

      const response = await axios.put(url, payload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });

      // Update the user in the local state
      setUsers((prevUsers) =>
        prevUsers.map((u) => (u.id === currentEditUser.id ? response.data : u)),
      );
      toast.success("Profile Updated Successfully");
      setShowEditModal(false);
    } catch (error) {
      toast.error("Failed to update user details. Please try again.");
      setEditError("Failed to save user details. Please try again.");
    } finally {
      setEditLoading(false);
    }
  };

  // Add the handleEditChange function
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setCurrentEditUser((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Add the toggleStatus function (replaces toggleBlock)
  const toggleStatus = () => {
    setCurrentEditUser((prev) => ({
      ...prev,
      isActive: !prev.isActive,
    }));
  };

  // Update the handleDelete function
  const handleDelete = (userId) => {
    const selectedUser = users.find((u) => u.id === userId);
    setUserToDelete(selectedUser);
    setShowDeleteModal(true);
  };

  // Add the confirmDelete function
  const confirmDelete = async () => {
    if (!userToDelete) return;

    try {
      setDeleteLoading(true);
      setDeleteError(null);

      // Determine the API URL based on the account type
      const url =
        user?.accountType === "SUPER_SUPER_ADMIN" ||
        user?.accountType === "SUPER_ADMIN" ||
        user?.accountType === "RESELLER"
          ? `${API_URL}/api/user/delete/${userToDelete.email}`
          : `${API_URL}/api/companyuser/delete/${userToDelete.id}`;

      await axios.delete(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });

      // Remove the user from the local state
      setUsers((prevUsers) =>
        prevUsers.filter((u) => u.id !== userToDelete.id),
      );
      setShowDeleteModal(false);
      fetchPermissions();
    } catch (error) {
      console.error("Error deleting user:", error);
      setDeleteError("Failed to delete user. Please try again.");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCancelButton = () => {
    setShowDeleteModal(false);
    setDeleteError("");
  };
  // Handle search
  const filteredUsers = users.filter((user) =>
    Object.values(user).some(
      (value) =>
        value &&
        value.toString().toLowerCase().includes(searchTerm.toLowerCase()),
    ),
  );

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const handlePasswordChange = async () => {
    if (!newPassword || !confirmNewPassword) {
      toast.error("Both fields are required");
      return;
    }
    if (newPassword !== confirmNewPassword) {
      toast.error("Passwords do not match");
      return;
    }

    try {
      const endpoint = isBusinessAccount
        ? `${API_URL}/api/user/set-password/${selectedUserForPasswordChange.id}/changedBy/${user.id}`
        : `${API_URL}/api/companyuser/set-password/${selectedUserForPasswordChange.id}/changedBy/${user.id}`;

      await axios.post(
        endpoint,
        {
          newPassword,
          confirmPassword: confirmNewPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );
      toast.success("✅ Password updated successfully");
      setShowPasswordModal(false);
      setNewPassword("");
      setConfirmNewPassword("");
    } catch (error) {
      console.error("Password update failed:", error);
      toast.error("❌ Failed to update password");
    }
  };

  // Group permissions by category for better organization in the modal
  const permissionGroups = [
    {
      title: "Account",
      permissions: ["createAccount", "viewAccount", "dashboard"],
    },
    {
      title: "Features",
      permissions: [
        "leads",
        "leadTask",
        "leadEdit",
        "task",
        "issues",
        "reminder",
        // "fileUpload",
        "meeting",
        "videoCall",
        "todo",
        "notes",
        "whatsapp",
        "calendar",
          "downloadReport", // NEW PERMISSION ADDED HERE
        ...(user?.accountType !== "COMPANY" ? ["invoice"] : []),
        "hr",
        "customer",
        "greetings",
        // "reports",
        "blackList",
      
      ],
    },
    ...(user?.accountType !== "COMPANY"
      ? [
          {
            title: "Settings",
            permissions: [
              "generalsettings",
              "ivrIntegration",
              "googleForm",
              "nnAcresConfig",
              "whatsappIntegration",
              "smsAutomation",
              "emailAutomation",
              "clickToCall",
            ],
          },
        ]
      : []), // Only show settings group if not COMPANY
    {
      title: "Attributes",
      permissions: [
        "attributes",
        "leadStatusAttributes",
        "leadSourceAttributes",
        "projectNameAttributes",
        "taskLabelAttributes",
        "taskStatusAttributes",
        "issuesStatusAttributes",
        "issuesType",
        "environmentAttributes",
        "severityAttributes",
      ],
    },
    {
      title: "Templates",
      permissions: [
        "templates",
        // "whatsappTemplates",
        "smsTemplates",
        "emailTemplates",
        "greetingTemplates",
      ],
    },
  ];

  return (
    <div className="container mx-auto shadow-lg">
      <div className="shadow-md bg-gray-100 dark:bg-gray-900 overflow-hidden">
        {/* Search and filter bar */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-col md:flex-row justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-4 md:mb-0">
            User Accounts
          </h2>
          <div className="relative w-full md:w-64">
            <input
              type="text"
              placeholder="Search users..."
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <div className="absolute left-3 top-2.5 text-gray-400">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4">
            <p>{error}</p>
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                >
                  Name
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                >
                  Email
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                >
                  Phone
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                >
                  Account Type
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                >
                  Company
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                >
                  Created By
                </th>
                {showSpecialFields && (
                  <>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                    >
                      Valid From
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                    >
                      Valid To
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                    >
                      SMS Rate
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                    >
                      Whatsapp Rate
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                    >
                      Email Rate
                    </th>
                  </>
                )}
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                >
                  Created Date
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td
                    colSpan={showSpecialFields ? 12 : 8}
                    className="px-6 py-4 text-center"
                  >
                    <div className="flex justify-center items-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    </div>
                  </td>
                </tr>
              ) : currentItems.length === 0 ? (
                <tr>
                  <td
                    colSpan={showSpecialFields ? 12 : 8}
                    className="px-6 py-4 text-center text-gray-500 dark:text-gray-400"
                  >
                    {error ? "Error loading data" : "No users found"}
                  </td>
                </tr>
              ) : (
                currentItems.map((item) => (
                  <tr
                    key={item.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {item.fname} {item.lname || "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {item.email || "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {item.mobileNumber || "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {item.accountType || "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {item.companyName || "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {item.accountCreatedByName || "-"}
                    </td>
                    {showSpecialFields && (
                      <>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {formatDate(item.validityFrom)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {formatDate(item.validityTo)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {item.smsSendRate || "-"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {item.whatsappSmsRate || "-"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {item.emailSendRate || "-"}
                        </td>
                      </>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(item.accountCreatedDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(item.id)}
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-500"
                          title="Edit"
                        >
                          <FiEdit className="h-5 w-5" />
                        </button>
                        {[
                          "SUPER_SUPER_ADMIN",
                          "SUPER_ADMIN",
                          "RESELLER",
                        ].includes(user?.accountType) && (
                          <button
                            onClick={() => handleAmount(item)}
                            className="text-yellow-600 dark:text-yellow-400 hover:text-yellow-900 dark:hover:text-yellow-500"
                            title="Amount"
                          >
                            <FaRupeeSign className="h-5 w-5" />
                          </button>
                        )}
                        <button
                          onClick={() => handlePermissions(item.id)}
                          className="text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-500"
                          title="Permissions"
                        >
                          <FiKey className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-500"
                          title="Delete"
                        >
                          <FiTrash2 className="h-5 w-5" />
                        </button>

                        {[
                          "SUPER_SUPER_ADMIN",
                          "SUPER_ADMIN",
                          "RESELLER",
                          "COMPANY",
                        ].includes(user?.accountType) && (
                          <button
                            onClick={() => {
                              setSelectedUserForPasswordChange(item);
                              setShowPasswordModal(true);
                            }}
                            className="text-purple-600 dark:text-purple-400 hover:text-purple-900 dark:hover:text-purple-500"
                            title="Change Password"
                          >
                            <FiEye className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="bg-white dark:bg-gray-800 px-4 py-3 flex flex-col sm:flex-row items-center justify-center sm:justify-between border-t border-gray-200 dark:border-gray-700 space-y-2 sm:space-y-0 sm:px-6">
          <div>
            <p className="text-sm text-gray-700 dark:text-gray-300 text-center sm:text-left">
              Showing{" "}
              <span className="font-medium">{indexOfFirstItem + 1}</span> to{" "}
              <span className="font-medium">
                {Math.min(indexOfLastItem, filteredUsers.length)}
              </span>{" "}
              of <span className="font-medium">{filteredUsers.length}</span>{" "}
              results
            </p>
          </div>
          <div className="w-full sm:w-auto overflow-x-auto">
            <nav
              className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
              aria-label="Pagination"
            >
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="sr-only">Previous</span>
                <FiChevronLeft className="h-5 w-5" />
              </button>

              {/* Always show first page */}
              <button
                onClick={() => setCurrentPage(1)}
                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                  currentPage === 1
                    ? "z-10 bg-blue-50 dark:bg-blue-900 border-blue-500 dark:border-blue-700 text-blue-600 dark:text-blue-300"
                    : "bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600"
                }`}
              >
                1
              </button>

              {/* Show ellipsis if current page is far from start */}
              {currentPage > 3 && (
                <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300">
                  ...
                </span>
              )}

              {/* Show pages around current page */}
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(
                  (number) =>
                    number > 1 &&
                    number < totalPages &&
                    Math.abs(number - currentPage) <= 1,
                )
                .map((number) => (
                  <button
                    key={number}
                    onClick={() => setCurrentPage(number)}
                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                      currentPage === number
                        ? "z-10 bg-blue-50 dark:bg-blue-900 border-blue-500 dark:border-blue-700 text-blue-600 dark:text-blue-300"
                        : "bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600"
                    }`}
                  >
                    {number}
                  </button>
                ))}

              {/* Show ellipsis if current page is far from end */}
              {currentPage < totalPages - 2 && (
                <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300">
                  ...
                </span>
              )}

              {/* Always show last page if there's more than one page */}
              {totalPages > 1 && (
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                    currentPage === totalPages
                      ? "z-10 bg-blue-50 dark:bg-blue-900 border-blue-500 dark:border-blue-700 text-blue-600 dark:text-blue-300"
                      : "bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600"
                  }`}
                >
                  {totalPages}
                </button>
              )}

              <button
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages}
                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="sr-only">Next</span>
                <FiChevronRight className="h-5 w-5" />
              </button>
            </nav>
          </div>
        </div>
      </div>

      {/* Permission Modal */}
      {showPermissionModal && currentUserPermissions && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="relative p-6">
              {/* Header */}
              <div className="sticky top-0 z-10 flex justify-between items-center border-b border-gray-200 dark:border-gray-700 pb-4 bg-white dark:bg-gray-800">
                <h3 className="text-xl font-semibold text-gray-800 dark:text-white">
                  Edit Permissions
                </h3>
                <button
                  onClick={() => setShowPermissionModal(false)}
                  className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                >
                  <FiX className="h-6 w-6" />
                </button>
              </div>

              {permissionError && (
                <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mt-4">
                  <p>{permissionError}</p>
                </div>
              )}

              {user?.accountType === "SUPER_SUPER_ADMIN" && (
                <button
                  onClick={() => {
                    const allPermissions = {};
                    permissionGroups.forEach((group) => {
                      group.permissions.forEach((permission) => {
                        allPermissions[permission] = true;
                      });
                    });
                    setCurrentUserPermissions((prev) => ({
                      ...prev,
                      ...allPermissions,
                    }));
                  }}
                  className="px-3 py-1 mt-2 text-sm bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  Select All
                </button>
              )}

              {/* TRANSCRIPTION API KEY INTEGRATION SECTION */}
              {["SUPER_SUPER_ADMIN", "SUPER_ADMIN", "RESELLER"].includes(
                user?.accountType
              ) && (
                <div className="mt-6 mb-6 p-5 border border-indigo-100 dark:border-gray-700 bg-indigo-50/50 dark:bg-gray-700/30 rounded-xl shadow-sm">
                  <h4 className="text-lg font-semibold text-gray-800 dark:text-white mb-3">
                    Transcription API Key Configuration
                  </h4>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-5">
                    <div className="flex items-center">
                      <span
                        className={`text-sm font-medium ${
                          transcriptionMode === "DEFAULT"
                            ? "text-indigo-600 dark:text-indigo-400"
                            : "text-gray-500 dark:text-gray-400"
                        }`}
                      >
                        Default
                      </span>
                      <button
                        type="button"
                        disabled={isTranscriptionLoading}
                        onClick={() =>
                          updateTranscriptionMode(
                            transcriptionMode === "DEFAULT" ? "OWN" : "DEFAULT"
                          )
                        }
                        className={`mx-3 relative inline-flex h-6 w-12 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 ${
                          transcriptionMode === "OWN"
                            ? "bg-indigo-600"
                            : "bg-gray-400"
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            transcriptionMode === "OWN"
                              ? "translate-x-7"
                              : "translate-x-1"
                          }`}
                        />
                      </button>
                      <span
                        className={`text-sm font-medium ${
                          transcriptionMode === "OWN"
                            ? "text-indigo-600 dark:text-indigo-400"
                            : "text-gray-500 dark:text-gray-400"
                        }`}
                      >
                        Own
                      </span>
                    </div>
                  </div>
                </div>
              )}
              {/* END TRANSCRIPTION API KEY INTEGRATION SECTION */}

              <div className="mt-6">
                {permissionGroups.map((group) => {
                  // If account type is SUPER_ADMIN, show all permissions
                  const visiblePermissions =
                    user.accountType === "SUPER_SUPER_ADMIN"
                      ? group.permissions
                      : group.permissions.filter((permission) =>
                          hasPermission(permission),
                        );

                  if (visiblePermissions.length === 0) return null; // Hide section if no permissions are visible

                  return (
                    <div key={group.title} className="mb-8">
                      <h4 className="text-lg font-medium text-gray-800 dark:text-white mb-4">
                        {group.title}
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {visiblePermissions.map((permission) => (
                          <div key={permission} className="flex items-center">
                            <input
                              type="checkbox"
                              id={permission}
                              checked={
                                currentUserPermissions[permission] || false
                              }
                              onChange={() =>
                                handlePermissionToggle(permission)
                              }
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded dark:bg-gray-700 dark:border-gray-600"
                            />
                            <label
                              htmlFor={permission}
                              className="ml-2 block text-sm text-gray-700 dark:text-gray-300"
                            >
                              {permission
                                .replace(/([A-Z])/g, " $1")
                                .replace(/^./, (str) => str.toUpperCase())}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setShowPermissionModal(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  onClick={savePermissions}
                  disabled={permissionLoading}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {permissionLoading ? (
                    <span className="flex items-center">
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Saving...
                    </span>
                  ) : (
                    <span className="flex items-center">
                      <FiSave className="mr-2" />
                      Save Permissions
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEditModal && currentEditUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="relative p-6">
              {/* Fixed Header */}
              <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 flex justify-between items-center border-b border-gray-200 dark:border-gray-700 px-6 py-4">
                <h3 className="text-xl font-semibold text-gray-800 dark:text-white">
                  Edit User Details
                </h3>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                >
                  <FiX className="h-6 w-6" />
                </button>
              </div>

              {editError && (
                <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mt-4">
                  <p>{editError}</p>
                </div>
              )}

              <div className="mt-6 space-y-4">
                {/* Account Type Field - Only shown for certain account types */}
                {(["SUPER_SUPER_ADMIN", "SUPER_ADMIN", "RESELLER"].includes(
                  user?.accountType,
                ) ||
                  (user?.accountType === "COMPANY" &&
                    [
                      "VICE_PRESIDENT",
                      "MANAGER",
                      "TEAM_LEAD",
                      "HR",
                      "EXECUTIVE",
                    ].includes(currentEditUser.accountType))) && (
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Account Type
                    </label>
                    <select
                      name="accountType"
                      value={currentEditUser.accountType || ""}
                      onChange={handleEditChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:text-white"
                      disabled={
                        user?.accountType === "COMPANY" &&
                        currentEditUser.accountType === "COMPANY"
                      }
                    >
                      {/* Options for SUPER users */}
                      {user?.accountType === "SUPER_SUPER_ADMIN" && (
                        <>
                          <option value="SUPER_ADMIN">Super Admin</option>
                          <option value="RESELLER">Reseller</option>
                          <option value="COMPANY">Company</option>
                        </>
                      )}
                      {user?.accountType === "SUPER_ADMIN" && (
                        <>
                          <option value="RESELLER">Reseller</option>
                          <option value="COMPANY">Company</option>
                        </>
                      )}

                      {user?.accountType === "RESELLER" && (
                        <>
                          <option value="COMPANY">Company</option>
                        </>
                      )}

                      {/* Options for COMPANY users */}
                      {user?.accountType === "COMPANY" && (
                        <>
                          <option value="VICE_PRESIDENT">Vice President</option>
                          <option value="MANAGER">Manager</option>
                          <option value="TEAM_LEAD">Team Lead</option>
                          <option value="HR">HR</option>
                          <option value="EXECUTIVE">Executive</option>
                        </>
                      )}
                    </select>
                  </div>
                )}

                {/* Company Name */}
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Company Name
                  </label>
                  <input
                    type="text"
                    name="companyName"
                    value={currentEditUser.companyName || ""}
                    onChange={handleEditChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      First Name
                    </label>
                    <input
                      type="text"
                      name="fname"
                      value={currentEditUser.fname || ""}
                      onChange={handleEditChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Last Name
                    </label>
                    <input
                      type="text"
                      name="lname"
                      value={currentEditUser.lname || ""}
                      onChange={handleEditChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Read-only Email Field */}
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={currentEditUser.email || ""}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:text-white bg-gray-100 dark:bg-gray-600 cursor-not-allowed"
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        Cannot be changed
                      </span>
                    </div>
                  </div>

                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Phone
                    </label>
                    <input
                      type="tel"
                      name="mobileNumber"
                      value={currentEditUser.mobileNumber || ""}
                      onChange={handleEditChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>

                {/* Special fields for admin users */}
                {showSpecialFields && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="relative">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Valid From
                        </label>
                        <input
                          type="date"
                          name="validityFrom"
                          value={
                            currentEditUser.validityFrom
                              ? new Date(currentEditUser.validityFrom)
                                  .toISOString()
                                  .split("T")[0]
                              : ""
                          }
                          onChange={handleEditChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:text-white"
                        />
                      </div>

                      <div className="relative">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Valid To
                        </label>
                        <input
                          type="date"
                          name="validityTo"
                          value={
                            currentEditUser.validityTo
                              ? new Date(currentEditUser.validityTo)
                                  .toISOString()
                                  .split("T")[0]
                              : ""
                          }
                          onChange={handleEditChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:text-white"
                        />
                      </div>
                    </div>

                    <div className="flex items-center">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300 mr-3">
                        Status:
                      </span>
                      <button
                        type="button"
                        onClick={toggleStatus}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                          currentEditUser.isActive
                            ? "bg-green-500"
                            : "bg-gray-200"
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            currentEditUser.isActive
                              ? "translate-x-6"
                              : "translate-x-1"
                          }`}
                        />
                      </button>
                      <span className="ml-2 text-sm text-gray-600 dark:text-gray-300">
                        {currentEditUser.isActive ? "Active" : "Blocked"}
                      </span>
                    </div>
                  </>
                )}
              </div>

              <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  onClick={saveEdit}
                  disabled={editLoading}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {editLoading ? (
                    <span className="flex items-center">
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Saving...
                    </span>
                  ) : (
                    <span className="flex items-center">
                      <FiSave className="mr-2" />
                      Save Changes
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && userToDelete && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-between items-center border-b border-gray-200 dark:border-gray-700 pb-4">
                <h3 className="text-xl font-semibold text-gray-800 dark:text-white">
                  Confirm Deletion
                </h3>
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                >
                  <FiX className="h-6 w-6" />
                </button>
              </div>

              {deleteError && (
                <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mt-4">
                  <p>{deleteError}</p>
                </div>
              )}

              <div className="mt-6">
                <p className="text-gray-700 dark:text-gray-300">
                  Are you sure you want to delete {userToDelete.fname}{" "}
                  {userToDelete.lname} ({userToDelete.email})?
                </p>
                <p className="text-red-500 dark:text-red-400 mt-2">
                  This action cannot be undone.
                </p>
              </div>

              <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={handleCancelButton}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={deleteLoading}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                >
                  {deleteLoading ? (
                    <span className="flex items-center">
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Deleting...
                    </span>
                  ) : (
                    <span>Delete</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showAmountModal && currentAmountUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl 
                w-full max-w-full sm:max-w-lg md:max-w-2xl lg:max-w-3xl 
                max-h-[90vh] overflow-y-auto"
          >
            <div className="relative p-4 sm:p-6">
              <div className="flex justify-between items-center border-b border-gray-200 dark:border-gray-700 pb-4">
                <h3 className="text-xl font-semibold text-gray-800 dark:text-white">
                  Amount Details
                </h3>
                <button
                  onClick={() => setShowAmountModal(false)}
                  className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                >
                  <FiX className="h-6 w-6" />
                </button>
              </div>

              {amountError && (
                <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mt-4">
                  <p>{amountError}</p>
                </div>
              )}

              <div className="mt-6 space-y-4">
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Staff Limit
                  </label>
                  <input
                    type="number"
                    name="staffLimit"
                    value={currentAmountUser.staffLimit || ""}
                    onChange={(e) =>
                      setCurrentAmountUser((prev) => ({
                        ...prev,
                        staffLimit: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      SMS Rate
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      name="smsSendRate"
                      value={currentAmountUser.smsSendRate || ""}
                      onChange={(e) =>
                        setCurrentAmountUser((prev) => ({
                          ...prev,
                          smsSendRate: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Email Rate
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      name="emailSendRate"
                      value={currentAmountUser.emailSendRate || ""}
                      onChange={(e) =>
                        setCurrentAmountUser((prev) => ({
                          ...prev,
                          emailSendRate: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Total SMS Amount
                    </label>
                    <input
                      type="number"
                      name="totalSMSAmount"
                      value={currentAmountUser.totalSMSAmount || ""}
                      onChange={(e) =>
                        setCurrentAmountUser((prev) => ({
                          ...prev,
                          totalSMSAmount: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Total Email Amount
                    </label>
                    <input
                      type="number"
                      name="totalEmailAmount"
                      value={currentAmountUser.totalEmailAmount || ""}
                      onChange={(e) =>
                        setCurrentAmountUser((prev) => ({
                          ...prev,
                          totalEmailAmount: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Whatsapp Rate
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      name="whatsappSmsRate"
                      value={currentAmountUser.whatsappSmsRate || ""}
                      onChange={(e) =>
                        setCurrentAmountUser((prev) => ({
                          ...prev,
                          whatsappSmsRate: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Total Whatsapp Amount
                    </label>
                    <input
                      type="number"
                      name="totalWhatsaappAmount"
                      value={currentAmountUser.totalWhatsaappAmount || ""}
                      onChange={(e) =>
                        setCurrentAmountUser((prev) => ({
                          ...prev,
                          totalWhatsaappAmount: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>

                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Balance Expiry Date
                  </label>
                  <input
                    type="date"
                    name="balanceExpiryDate"
                    value={
                      currentAmountUser.balanceExpiryDate
                        ? new Date(currentAmountUser.balanceExpiryDate)
                            .toISOString()
                            .split("T")[0]
                        : ""
                    }
                    onChange={(e) =>
                      setCurrentAmountUser((prev) => ({
                        ...prev,
                        balanceExpiryDate: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:text-white"
                    min={new Date().toISOString().split("T")[0]}
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setShowAmountModal(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  onClick={saveAmount}
                  disabled={amountLoading}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {amountLoading ? (
                    <span className="flex items-center">
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Saving...
                    </span>
                  ) : (
                    <span className="flex items-center">
                      <FiSave className="mr-2" />
                      Save Amount
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showPasswordModal && selectedUserForPasswordChange && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center">
          <div className="bg-white dark:bg-gray-900 p-6 rounded-2xl w-full max-w-md shadow-xl relative">
            <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white text-center">
              Change Password for {selectedUserForPasswordChange.fname}{" "}
              {selectedUserForPasswordChange.lname}
            </h2>

            <div className="mb-4 relative">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200">
                New Password
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full p-2 pr-10 border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-md shadow-sm mt-1 text-gray-900 dark:text-white"
                />
                <span
                  className="absolute inset-y-0 right-2 flex items-center cursor-pointer text-gray-500 dark:text-gray-300"
                  onClick={() => setShowNewPassword((prev) => !prev)}
                >
                  {showNewPassword ? <FaEyeSlash /> : <FaEyeSolid />}
                </span>
              </div>
            </div>

            <div className="mb-4 relative">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  className={`w-full p-2 pr-10 border ${
                    newPassword &&
                    confirmNewPassword &&
                    newPassword !== confirmNewPassword
                      ? "border-red-500"
                      : "border-gray-300 dark:border-gray-700"
                  } bg-white dark:bg-gray-800 rounded-md shadow-sm mt-1 text-gray-900 dark:text-white`}
                />
                <span
                  className="absolute inset-y-0 right-2 flex items-center cursor-pointer text-gray-500 dark:text-gray-300"
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                >
                  {showConfirmPassword ? <FaEyeSlash /> : <FaEyeSolid />}
                </span>
              </div>
              {newPassword &&
                confirmNewPassword &&
                newPassword !== confirmNewPassword && (
                  <p className="text-red-500 text-xs mt-1">
                    Passwords do not match
                  </p>
                )}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                className="px-4 py-2 rounded-md bg-gray-400 hover:bg-gray-500 text-white"
                onClick={() => {
                  setShowPasswordModal(false);
                  setNewPassword("");
                  setConfirmNewPassword("");
                }}
              >
                Cancel
              </button>
              <button
                onClick={handlePasswordChange}
                className="px-4 py-2 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewAccount;